import { useEffect, useRef, useState, useCallback, useContext } from "react";
import { StyledContainer, LinkInfoContainer, DocumentLinkingContainer } from "./styles";
import Dropdown from "../../../Dropdown";
import InlineSVG from "react-inlinesvg";
import { useProjectStore } from "../../../../stores/projectStore";
import { useStoryBibleStore } from "../../../../stores/storyBibleStore";
import { useStructuredOutlineStore } from "../../../StoryBible/StructuredOutline/StructuredOutlineStore";
import { useSeriesStore } from "../../../../stores/seriesStore";
import classNames from "classnames";
import TextButton from "src/components/TextButton";
import useOnClickOutside from "src/hooks/useOnClickOutside";
import IconButton from "src/components/IconButton";
import { useSudowriteStore } from "src/stores/sudowriteStore";
import { EditorContainerContext } from "../EditorContainerContext";

const DISCONNECT_OPTION = { label: "Disconnect", value: null, danger: true };

function PrevDocumentLinking({ project, documentId }) {
  const [documents, setDocuments] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(undefined); // undefined means no selection, can't use null because it's used for the disconnect option
  const [showPrevDocumentDropdown, setShowPrevDocumentDropdown] = useState(false);
  const document = useProjectStore((state) => state.document);
  const [locallyDisconnected, setLocallyDisconnected] = useState(false);
  const userSelectionRef = useRef(null);

  // needed to optimistically calculate the previous document based on linked outline section
  const scenes = useStoryBibleStore((state) => state.scenes);
  const sceneId = useStoryBibleStore((state) => state.sceneId);
  const buildChapterOptions = useStructuredOutlineStore((state) => state.buildChapterOptions);
  const subscription = useSudowriteStore((state) => state.subscription);

  const hasPrevDocument = document?.previousId !== null && !locallyDisconnected;

  useEffect(() => {
    setSelectedDocument(undefined);
    setShowPrevDocumentDropdown(false);
    setLocallyDisconnected(false);
    userSelectionRef.current = null;
  }, [documentId]);

  const savePreviousDocument = async (option) => {
    // Store user's selection to prevent auto-selection from overriding it
    userSelectionRef.current = option;

    if (option.value === DISCONNECT_OPTION.value) {
      setLocallyDisconnected(true);
      setSelectedDocument(undefined);
      setShowPrevDocumentDropdown(false);
    }

    if (option.value) {
      setSelectedDocument(option);
      setLocallyDisconnected(false);
    }

    const updatedDocument = await project.updateDocument({ id: documentId, previousId: option.value });
  };

  const setBestGuessPreviousDocument = useCallback(async () => {
    // Skip if user has made a selection (and it's not the disconnect option)
    if (userSelectionRef.current && userSelectionRef.current.value !== DISCONNECT_OPTION.value) return;

    // Don't try to find a previous document if we aren't connected to a chapter in the outline
    const chapterId = scenes[sceneId]?.itemId;
    if (!chapterId) return;

    const chapters = buildChapterOptions();
    const previousDocumentId = await project.getPreviousDocumentIdFromChapter({
      chapterId,
      chapters,
      scenes,
      userId: subscription.id,
    });
    const documentToSelect = documents.find((d) => d.value === previousDocumentId && d.value !== DISCONNECT_OPTION.value);
    if (documentToSelect) {
      setSelectedDocument(documentToSelect);
    }
  }, [scenes, sceneId, buildChapterOptions, subscription, project, documents]);

  const handleClickConnectIt = () => {
    if (selectedDocument && selectedDocument.value) {
      // we have optimistically selected a document, save it
      project.updateDocument({ id: documentId, previousId: selectedDocument.value });
    }
    setShowPrevDocumentDropdown(true);
  };

  useEffect(() => {
    // get all documents in the same order they are in Left Bar
    // excludes current document + folders, while maintaining folder order
    const documentOptions = project.getFlattenedDocuments(documentId);
    setDocuments([...documentOptions, DISCONNECT_OPTION]);
  }, [project, documentId]);

  useEffect(() => {
    // Skip if user has made a selection (and it's not the disconnect option)
    if (userSelectionRef.current && userSelectionRef.current.value !== DISCONNECT_OPTION.value) return;

    if (hasPrevDocument) {
      const selectedDocument = documents.find((d) => d.value === document.previousId && d.value !== DISCONNECT_OPTION.value);
      selectedDocument && setSelectedDocument(selectedDocument);
    } else {
      setBestGuessPreviousDocument();
    }
  }, [document.previousId, documentId, documents, hasPrevDocument, setBestGuessPreviousDocument]);

  return (
    <>
      {(hasPrevDocument || showPrevDocumentDropdown) && (
        <LinkInfoContainer>
          <InlineSVG src={"/assets/continues.svg"} />
          <p>Continues from the end of</p>
          <Dropdown
            options={documents}
            selectedOption={selectedDocument}
            onSelect={savePreviousDocument}
            placeholder="Select Document"
          />
        </LinkInfoContainer>
      )}
      {!hasPrevDocument && !showPrevDocumentDropdown && (
        <LinkInfoContainer>
          <InlineSVG src={"/assets/continues.svg"} />
          <p>Does this document continue another one?</p>
          {!showPrevDocumentDropdown && (
            <TextButton onClick={handleClickConnectIt}>
              <p className="text-button-text">Connect it</p>
            </TextButton>
          )}
        </LinkInfoContainer>
      )}
    </>
  );
}
function DraftLinking({ documentId }) {
  const setProseGeneratorActive = useStoryBibleStore((state) => state.setProseGeneratorActive);
  const setShowLinkingMenu = useProjectStore((state) => state.setShowLinkingMenu);
  const hasDocumentScenes = useStoryBibleStore((state) => state.hasDocumentScenes(documentId));

  const handleAddScenes = () => {
    setShowLinkingMenu(false);
    setProseGeneratorActive(true);
  };

  return (
    <LinkInfoContainer>
      <InlineSVG src={"/assets/story-bible/generator.svg"} />
      <p>{hasDocumentScenes ? "You have scenes for this document." : "You don't have any scenes for this document yet."}</p>
      <TextButton onClick={handleAddScenes}>
        <p className="text-button-text">{hasDocumentScenes ? "View Scenes" : "Add Scenes"}</p>
      </TextButton>
    </LinkInfoContainer>
  );
}

function OutlineLinking({ documentId }) {
  const features = useSudowriteStore((state) => state.getFeatureFlags)();
  const { hasAccessToPOVAndTenseBeta } = features || {};

  const [chapterOptions, setChapterOptions] = useState([]);
  const [selectedChapter, setSelectedChapter] = useState(undefined); // undefined means no selection, can't use null because it's used for the disconnect option
  const [showChapterDropdown, setShowChapterDropdown] = useState(false);

  const sceneId = useStoryBibleStore((state) => state.sceneId);
  const sceneToWrite = useStoryBibleStore((state) => state.sceneToWrite);
  const setScenes = useStoryBibleStore((state) => state.setScenes);
  const triggerImmediateSave = useStoryBibleStore((state) => state.triggerImmediateSave);
  const isActive = useStoryBibleStore((state) => state.isActive);
  const orphanedChapter = useStoryBibleStore((state) => state.orphanedChapter);
  const buildChapterOptions = useStructuredOutlineStore((state) => state.buildChapterOptions);
  const outline = useStructuredOutlineStore((state) => state.outline);
  const project = useProjectStore((state) => state.project);
  const selectedProjects = useSeriesStore((state) => state.selectedProjects);
  const setDocumentPOVTense = useProjectStore((state) => state.setDocumentPOVTense);

  const isViewingCurrentProjectOutline = project.seriesId ? selectedProjects.outline?.projectId === project.id : true;

  async function selectStructuredOutlineChapter(chapter) {
    if (chapter.value === DISCONNECT_OPTION.value) {
      // reset the chapter selector dropdown
      setSelectedChapter(undefined);
      setShowChapterDropdown(false);
    }

    setScenes((draft) => {
      draft[sceneId].itemId = chapter.value;
    });
    triggerImmediateSave?.();

    // Change the povTenseId on the document to the chapter's povTenseId whenever you link a document
    if (hasAccessToPOVAndTenseBeta) {
      await setDocumentPOVTense({ documentId, povTenseId: chapter.povTenseId, disconnect: chapter.value === DISCONNECT_OPTION.value });
    }
  }

  useEffect(() => {
    setSelectedChapter(undefined);
    setShowChapterDropdown(false);
  }, [documentId, sceneId]);

  useEffect(() => {
    const currentChapterOptions = buildChapterOptions();
    setChapterOptions([...currentChapterOptions, DISCONNECT_OPTION]);

    if (sceneToWrite) {
      if (!isViewingCurrentProjectOutline) {
        setSelectedChapter({ label: sceneToWrite.label, value: sceneToWrite.value });
        setChapterOptions([]);
        return;
      }

      const selectedChapter = currentChapterOptions.find((c) => c.value === sceneToWrite.value);
      setSelectedChapter(selectedChapter);
    } else {
      // reset the selected chapter if the document is not connected to the outline
      setSelectedChapter(undefined);
    }
  }, [sceneToWrite, outline, isViewingCurrentProjectOutline]);

  return (
    <>
      {/* Document is linked to an outline section*/}
      {sceneToWrite && (
        <LinkInfoContainer className={classNames({ disabled: !isActive || !isViewingCurrentProjectOutline })}>
          <InlineSVG src={"/assets/chain-linked.svg"} />
          <p>Linked to</p>
          <Dropdown
            options={chapterOptions}
            selectedOption={selectedChapter}
            onSelect={selectStructuredOutlineChapter}
          />
          <p>{`in your Outline${isActive ? "" : " Story Bible is off."}`}</p>
        </LinkInfoContainer>
      )}
      {/* Outline exists, Document is not linked to an outline section, and has never been linked to the outline */}
      {!sceneToWrite && outline?.content?.items?.length > 0 && !orphanedChapter && (
        <LinkInfoContainer className={classNames({ disabled: !isActive || !isViewingCurrentProjectOutline })}>
          <InlineSVG src={"/assets/chain-linked.svg"} />
          <p>Is this document in your Outline?</p>
          {!showChapterDropdown && (
            <TextButton onClick={() => setShowChapterDropdown(true)}>
              <p className="text-button-text">Link it</p>
            </TextButton>
          )}
          {!isActive && <p>Story Bible is off.</p>}
          {showChapterDropdown && (
            <Dropdown
              options={chapterOptions}
              selectedOption={selectedChapter}
              onSelect={selectStructuredOutlineChapter}
              placeholder="Select Chapter"
            />
          )}
        </LinkInfoContainer>
      )}
      {/* Outline exists, Document is not linked to an outline section, and has been linked to the outline but the outline section was removed */}
      {!sceneToWrite && outline?.content?.items?.length > 0 && orphanedChapter && (
        <LinkInfoContainer className={classNames({ disabled: !isActive || !isViewingCurrentProjectOutline })}>
          <InlineSVG src={"/assets/chain-unlinked.svg"} />
          <p>Connected to a Chapter that doesn't exist in your Outline anymore.</p>
          {!showChapterDropdown && (
            <a
              href="#"
              onClick={() => setShowChapterDropdown(true)}
            >
              Re-connect
            </a>
          )}
          {!isActive && <p>Story Bible is off.</p>}
          {showChapterDropdown && (
            <Dropdown
              options={chapterOptions}
              selectedOption={selectedChapter}
              onSelect={selectStructuredOutlineChapter}
              placeholder="select chapter"
            />
          )}
        </LinkInfoContainer>
      )}
      {/* Outline does not exist */}
      {!sceneToWrite && outline?.content?.items?.length === 0 && (
        <LinkInfoContainer className="disabled">
          <InlineSVG src={"/assets/chain-linked.svg"} />
          <p>You don't have an Outline, but if you did, you could link it here.</p>
        </LinkInfoContainer>
      )}
    </>
  );
}

export default function DocumentLinkingMenu({ project, documentId, hideLinkingMenu, inTrashView }) {
  // show-hide logic
  const hasMultipleDocs = project.contents.length > 1;
  const ref = useRef(null);
  const { height, menuPosition, setMenuPosition, title } = useContext(EditorContainerContext);
  const [isClosing, setIsClosing] = useState(false);
  const isLoading = useStructuredOutlineStore((state) => state.isLoading);
  const isLoadingSceneToWrite = useStoryBibleStore((state) => state.isLoadingSceneToWrite);
  const showLinkingMenu = useProjectStore((state) => state.showLinkingMenu);
  const setShowLinkingMenu = useProjectStore((state) => state.setShowLinkingMenu);

  const actuallyShowLinkingMenu = (showLinkingMenu || isClosing) && !isLoading && !isLoadingSceneToWrite && !inTrashView;
  const { offset } = height || {};
  const [buttonPosition, setButtonPosition] = useState(0);

  useOnClickOutside(ref, hideLinkingMenu);

  useEffect(() => {
    if (!showLinkingMenu) {
      setIsClosing(true);
      setTimeout(() => {
        setIsClosing(false);
      }, 300);
    }
  }, [showLinkingMenu]);

  const handleOnClick = (event) => {
    setShowLinkingMenu(!showLinkingMenu);
    if (showLinkingMenu) {
      setMenuPosition();
    }
  };

  useEffect(() => {
    if (!ref.current) return;

    const editorSection = ref.current.closest("#editor-section");
    if (!editorSection) return;

    const titleWrapper = editorSection.querySelector("#title-text-wrapper");
    const button = ref.current.querySelector(".linking-menu-button");
    if (!titleWrapper || !button) return;

    const titleWrapperBox = titleWrapper.getBoundingClientRect();
    const buttonBox = button.getBoundingClientRect();
    setButtonPosition(titleWrapperBox.left - (buttonBox.left - 8 + buttonBox.width / 2));
  }, [ref, title, isClosing]);

  return (
    <DocumentLinkingContainer menuPosition={menuPosition}>
      <div ref={ref} className="document-linking-menu-container">
        {!inTrashView && (
          <>
            <IconButton
              src="/assets/hotdog-menu.svg"
              onClick={handleOnClick}
              ariaLabel="open document linking menu"
              iconSize={16}
              className="linking-menu-button"
              style={{ top: offset ?? 0 }}
            />
            <StyledContainer
              id="document-linking-menu"
              actuallyShowLinkingMenu={actuallyShowLinkingMenu}
              menuPosition={menuPosition}
              buttonPosition={buttonPosition}
              className={isClosing ? "closing" : "opening"}
              style={{ transformOrigin: menuPosition === "down" ? "top right" : menuPosition === "down-centered" ? "center center" : "top left" }}
            >
              <OutlineLinking documentId={documentId} />
              {hasMultipleDocs && (
                <PrevDocumentLinking
                  project={project}
                  documentId={documentId}
                />
              )}
              <DraftLinking documentId={documentId} />
            </StyledContainer>
          </>
        )}
      </div>
    </DocumentLinkingContainer>
  );
}
